import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Phone, PhoneOff, PhoneIncoming, PhoneMissed, PhoneOutgoing,
  Search, Mic, MicOff, Volume2, VolumeX, Clock, User, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' }
];

export const AudioCallsPage = () => {
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const [contacts, setContacts] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [callState, setCallState] = useState('idle'); // idle, calling, ringing, connected
  const [currentCall, setCurrentCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOff, setIsSpeakerOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  // Load contacts and history
  useEffect(() => {
    loadContacts();
    loadCallHistory();
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (token) {
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      cleanupCall();
    };
  }, [token]);

  // Call duration timer
  useEffect(() => {
    if (callState === 'connected') {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      setCallDuration(0);
    }
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, [callState]);

  const connectWebSocket = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}/api/ws/audio/${token}`);

    ws.onopen = () => {
      console.log('WebSocket audio connected');
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleSignalingMessage(message);
    };

    ws.onclose = () => {
      console.log('WebSocket audio disconnected');
      // Reconnect after 3s
      reconnectTimerRef.current = setTimeout(() => {
        if (token) connectWebSocket();
      }, 3000);
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
    };

    wsRef.current = ws;
  }, [token]);

  const handleSignalingMessage = useCallback(async (message) => {
    switch (message.type) {
      case 'call_offer':
        handleIncomingCall(message);
        break;
      case 'call_ringing':
        setCallState('calling');
        break;
      case 'call_answer':
        await handleCallAnswer(message);
        break;
      case 'ice_candidate':
        handleRemoteIceCandidate(message);
        break;
      case 'call_ended':
        handleCallEnded(message);
        break;
      case 'call_rejected':
        handleCallRejected();
        break;
      case 'call_unavailable':
        handleCallUnavailable(message);
        break;
      case 'presence':
        handlePresenceUpdate(message);
        break;
      default:
        break;
    }
  }, []);

  const handlePresenceUpdate = (message) => {
    setOnlineUsers(prev => {
      const updated = new Set(prev);
      if (message.is_online) {
        updated.add(message.user_id);
      } else {
        updated.delete(message.user_id);
      }
      return updated;
    });
    // Update contacts list
    setContacts(prev => prev.map(c => ({
      ...c,
      is_online: message.user_id === c.id ? message.is_online : c.is_online
    })));
  };

  const loadContacts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/calls/contacts`);
      setContacts(res.data);
      const onlineSet = new Set(res.data.filter(c => c.is_online).map(c => c.id));
      setOnlineUsers(onlineSet);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    }
  };

  const loadCallHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/calls/history`);
      setCallHistory(res.data);
    } catch (err) {
      console.error('Failed to load call history:', err);
    }
  };

  const startCall = async (contact) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;

      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      pcRef.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current) {
          wsRef.current.send(JSON.stringify({
            type: 'ice_candidate',
            target_id: contact.id,
            candidate: event.candidate
          }));
        }
      };

      pc.ontrack = (event) => {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.streams[0];
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      setCurrentCall({
        contact,
        callId: null,
        direction: 'outgoing'
      });
      setCallState('calling');

      wsRef.current.send(JSON.stringify({
        type: 'call_offer',
        target_id: contact.id,
        caller_name: `${user.first_name} ${user.last_name}`,
        sdp: offer
      }));
    } catch (err) {
      console.error('Failed to start call:', err);
      cleanupCall();
    }
  };

  const handleIncomingCall = (message) => {
    const callerContact = contacts.find(c => c.id === message.caller_id) || {
      id: message.caller_id,
      first_name: message.caller_name?.split(' ')[0] || '',
      last_name: message.caller_name?.split(' ').slice(1).join(' ') || '',
    };

    setCurrentCall({
      contact: callerContact,
      callId: message.call_id,
      direction: 'incoming',
      sdp: message.sdp
    });
    setCallState('ringing');
  };

  const answerCall = async () => {
    if (!currentCall) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;

      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      pcRef.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current) {
          wsRef.current.send(JSON.stringify({
            type: 'ice_candidate',
            target_id: currentCall.contact.id,
            candidate: event.candidate
          }));
        }
      };

      pc.ontrack = (event) => {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.streams[0];
        }
      };

      if (currentCall.sdp) {
        await pc.setRemoteDescription(new RTCSessionDescription(currentCall.sdp));
      }

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      wsRef.current.send(JSON.stringify({
        type: 'call_answer',
        call_id: currentCall.callId,
        sdp: answer
      }));

      setCallState('connected');
    } catch (err) {
      console.error('Failed to answer call:', err);
      cleanupCall();
    }
  };

  const handleCallAnswer = async (message) => {
    if (pcRef.current && message.sdp) {
      try {
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(message.sdp));
        setCallState('connected');
        setCurrentCall(prev => prev ? { ...prev, callId: message.call_id || prev.callId } : null);
      } catch (err) {
        console.error('Failed to set remote description:', err);
      }
    }
  };

  const handleRemoteIceCandidate = async (message) => {
    if (pcRef.current && message.candidate) {
      try {
        await pcRef.current.addIceCandidate(new RTCIceCandidate(message.candidate));
      } catch (err) {
        console.error('Failed to add ICE candidate:', err);
      }
    }
  };

  const endCall = () => {
    if (wsRef.current && currentCall) {
      wsRef.current.send(JSON.stringify({
        type: 'call_end',
        call_id: currentCall.callId
      }));
    }
    cleanupCall();
    loadCallHistory();
  };

  const rejectCall = () => {
    if (wsRef.current && currentCall) {
      wsRef.current.send(JSON.stringify({
        type: 'call_reject',
        call_id: currentCall.callId
      }));
    }
    cleanupCall();
  };

  const handleCallEnded = () => {
    cleanupCall();
    loadCallHistory();
  };

  const handleCallRejected = () => {
    cleanupCall();
  };

  const handleCallUnavailable = (message) => {
    cleanupCall();
  };

  const cleanupCall = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    setCallState('idle');
    setCurrentCall(null);
    setIsMuted(false);
    setIsSpeakerOff(false);
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleSpeaker = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !remoteAudioRef.current.muted;
      setIsSpeakerOff(!isSpeakerOff);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatCallTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredContacts = contacts.filter(c => {
    if (!searchQuery) return true;
    const name = `${c.first_name} ${c.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase()) || c.email?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getInitials = (contact) => {
    return `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.toUpperCase();
  };

  const labels = {
    fr: {
      audioCalls: 'Appels Audio',
      contacts: 'Contacts',
      history: 'Historique',
      searchContacts: 'Rechercher un contact...',
      call: 'Appeler',
      online: 'En ligne',
      offline: 'Hors ligne',
      calling: 'Appel en cours...',
      ringing: 'Appel entrant...',
      connected: 'En communication',
      endCall: 'Raccrocher',
      answer: 'Répondre',
      reject: 'Refuser',
      noContacts: 'Aucun contact disponible',
      noHistory: 'Aucun historique d\'appels',
      mute: 'Muet',
      unmute: 'Activer le micro',
      incoming: 'Entrant',
      outgoing: 'Sortant',
      missed: 'Manqué',
      completed: 'Terminé',
      rejected: 'Refusé',
    },
    en: {
      audioCalls: 'Audio Calls',
      contacts: 'Contacts',
      history: 'History',
      searchContacts: 'Search contacts...',
      call: 'Call',
      online: 'Online',
      offline: 'Offline',
      calling: 'Calling...',
      ringing: 'Incoming call...',
      connected: 'Connected',
      endCall: 'End Call',
      answer: 'Answer',
      reject: 'Reject',
      noContacts: 'No contacts available',
      noHistory: 'No call history',
      mute: 'Mute',
      unmute: 'Unmute',
      incoming: 'Incoming',
      outgoing: 'Outgoing',
      missed: 'Missed',
      completed: 'Completed',
      rejected: 'Rejected',
    }
  };
  const l = labels[language] || labels.fr;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="audio-calls-page">
      {/* Hidden audio element for remote stream */}
      <audio ref={remoteAudioRef} autoPlay playsInline data-testid="remote-audio" />

      {/* Active Call Overlay */}
      {callState !== 'idle' && currentCall && (
        <div className="fixed inset-0 z-50 bg-[var(--tenant-primary)]/95 flex items-center justify-center" data-testid="call-overlay">
          <div className="text-center text-white space-y-6 px-4">
            <div className="w-24 h-24 mx-auto rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <span className="text-3xl font-bold">{getInitials(currentCall.contact)}</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold">
                {currentCall.contact.first_name} {currentCall.contact.last_name}
              </h2>
              <p className="text-white/70 mt-1">
                {callState === 'calling' && l.calling}
                {callState === 'ringing' && l.ringing}
                {callState === 'connected' && formatDuration(callDuration)}
              </p>
            </div>

            {/* Call pulse animation */}
            {(callState === 'calling' || callState === 'ringing') && (
              <div className="flex justify-center">
                <div className="w-4 h-4 bg-white/50 rounded-full animate-ping" />
              </div>
            )}

            {/* Call controls */}
            <div className="flex items-center justify-center gap-6 pt-4">
              {callState === 'connected' && (
                <>
                  <button
                    onClick={toggleMute}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isMuted ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'
                    }`}
                    data-testid="mute-button"
                  >
                    {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                  </button>
                  <button
                    onClick={toggleSpeaker}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isSpeakerOff ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'
                    }`}
                    data-testid="speaker-button"
                  >
                    {isSpeakerOff ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                  </button>
                </>
              )}

              {callState === 'ringing' && (
                <button
                  onClick={answerCall}
                  className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center hover:bg-green-600 transition-colors animate-pulse"
                  data-testid="answer-call-button"
                >
                  <Phone className="w-7 h-7" />
                </button>
              )}

              <button
                onClick={callState === 'ringing' ? rejectCall : endCall}
                className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors"
                data-testid="end-call-button"
              >
                <PhoneOff className="w-7 h-7" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="audio-calls-title">{l.audioCalls}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {contacts.filter(c => c.is_online).length} {l.online}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="contacts" data-testid="calls-tabs">
        <TabsList className="grid w-full grid-cols-2 max-w-xs">
          <TabsTrigger value="contacts" data-testid="tab-contacts">{l.contacts}</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">{l.history}</TabsTrigger>
        </TabsList>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="mt-4">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={l.searchContacts}
                className="pl-10"
                data-testid="search-contacts-input"
              />
            </div>

            <div className="space-y-2">
              {/* Online contacts first */}
              {filteredContacts
                .sort((a, b) => (b.is_online ? 1 : 0) - (a.is_online ? 1 : 0))
                .map(contact => (
                <div
                  key={contact.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                  data-testid={`contact-${contact.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] text-sm font-medium">
                          {getInitials(contact)}
                        </AvatarFallback>
                      </Avatar>
                      <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                        contact.is_online ? 'bg-green-500' : 'bg-gray-300'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {contact.first_name} {contact.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{contact.role}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={contact.is_online ? 'default' : 'outline'}
                    onClick={() => startCall(contact)}
                    disabled={callState !== 'idle' || !contact.is_online}
                    className={contact.is_online ? 'bg-green-600 hover:bg-green-700' : ''}
                    data-testid={`call-button-${contact.id}`}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    {l.call}
                  </Button>
                </div>
              ))}

              {filteredContacts.length === 0 && (
                <div className="text-center py-12 text-gray-500" data-testid="no-contacts">
                  <Phone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{l.noContacts}</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <div className="space-y-2">
            {callHistory.map(call => (
              <div
                key={call.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100"
                data-testid={`call-history-${call.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    call.status === 'completed' ? 'bg-green-50' :
                    call.status === 'rejected' ? 'bg-red-50' : 'bg-yellow-50'
                  }`}>
                    {call.direction === 'outgoing' ? (
                      <PhoneOutgoing className={`w-5 h-5 ${
                        call.status === 'completed' ? 'text-green-600' : 'text-red-500'
                      }`} />
                    ) : call.status === 'rejected' || call.status === 'disconnected' ? (
                      <PhoneMissed className="w-5 h-5 text-red-500" />
                    ) : (
                      <PhoneIncoming className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {call.other_user
                        ? `${call.other_user.first_name} ${call.other_user.last_name}`
                        : language === 'fr' ? 'Utilisateur inconnu' : 'Unknown user'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="text-xs">
                        {call.direction === 'outgoing' ? l.outgoing : l.incoming}
                      </Badge>
                      <span className="text-xs text-gray-400">{formatCallTime(call.started_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${
                    call.status === 'completed' ? 'bg-green-100 text-green-700' :
                    call.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {l[call.status] || call.status}
                  </Badge>
                  {call.other_user && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const contact = contacts.find(c => c.id === call.other_user.id);
                        if (contact) startCall(contact);
                      }}
                      disabled={callState !== 'idle'}
                      data-testid={`recall-button-${call.id}`}
                    >
                      <Phone className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {callHistory.length === 0 && (
              <div className="text-center py-12 text-gray-500" data-testid="no-history">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{l.noHistory}</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AudioCallsPage;
